/**
 * FERRAMENTA CENTRAL DE BACKUP E RESTAURAÇÃO — RESTORE SERVICE
 * PROJETO: INSPECT IA
 * ESCOPO: ADMINISTRAÇÃO > BACKUP E RESTAURAÇÃO
 */

import type { RequestContext } from "../context/request-context.js";
import type { JsonObject } from "../../shared/kernel/json.js";
import { PermissionCode } from "../../domain/identity/access-types.js";
import type { AuthorizationService } from "../../permissions/authorization.js";
import type { AuditRecorder } from "../../audit/audit-recorder.js";
import { AuditAction } from "../../audit/audit-log.js";
import { AppError, ErrorCode } from "../../shared/errors/app-error.js";
import {
  CURRENT_SCHEMA_VERSION,
  calculateSha256,
  generateRestoreConfirmationToken,
  validateManifestStructure,
  type BackupPackage,
  type BackupValidationResult,
  type RestoreExecutionParams,
  type RestoreExecutionResult,
  type RestoreHistoryEntry
} from "../../domain/backup/backup-models.js";
import type { BackupRepository } from "../../repositories/backup-repository.js";
import type { BackupEntityCollector, BackupService } from "./backup-service.js";

export class RestoreService {
  constructor(
    private readonly backupRepo: BackupRepository,
    private readonly backupService: BackupService,
    private readonly auth: AuthorizationService,
    private readonly audit?: AuditRecorder
  ) {}

  async validateBackupPackage(
    context: RequestContext,
    rawContentOrPackage: string | BackupPackage
  ): Promise<BackupValidationResult> {
    this.auth.require(context, PermissionCode.BackupRestore);

    let pkg: BackupPackage;
    if (typeof rawContentOrPackage === "string") {
      try {
        pkg = JSON.parse(rawContentOrPackage) as BackupPackage;
      } catch {
        return {
          valid: false,
          errors: ["Formato de arquivo inválido. O pacote deve ser um JSON válido."],
          warnings: [],
          compatible: false,
          entitySummary: {},
          integrityChecked: false
        };
      }
    } else {
      pkg = rawContentOrPackage;
    }

    const { valid: manifestValid, errors: manifestErrors } = validateManifestStructure(pkg.manifest);
    if (!manifestValid || !pkg.manifest) {
      return {
        valid: false,
        errors: manifestErrors,
        warnings: [],
        compatible: false,
        entitySummary: {},
        integrityChecked: false
      };
    }

    const errors: string[] = [...manifestErrors];
    const warnings: string[] = [];

    // Check schema compatibility
    const compatible = pkg.manifest.schemaVersion <= CURRENT_SCHEMA_VERSION;
    if (!compatible) {
      errors.push(
        `Versão de schema incompatível: o backup usa schema v${pkg.manifest.schemaVersion}, mas o sistema atual suporta até v${CURRENT_SCHEMA_VERSION}.`
      );
    }

    // Check SHA-256 payload integrity
    let integrityChecked = false;
    if (pkg.databasePayload) {
      const payloadToHash = JSON.stringify({
        database: pkg.databasePayload,
        scope: pkg.manifest.scope,
        clientId: pkg.manifest.clientId
      });
      const calculatedHash = calculateSha256(payloadToHash);
      integrityChecked = calculatedHash === pkg.manifest.sha256Checksum;

      if (!integrityChecked) {
        errors.push("Falha na verificação de integridade: Checksum SHA-256 do payload não confere com o manifesto.");
      }
    }

    const entitySummary: Record<string, number> = {};
    if (pkg.databasePayload) {
      for (const [key, records] of Object.entries(pkg.databasePayload)) {
        entitySummary[key] = Array.isArray(records) ? records.length : 0;
      }
    }

    return {
      valid: errors.length === 0,
      manifest: pkg.manifest,
      errors,
      warnings,
      compatible,
      entitySummary,
      integrityChecked
    };
  }

  async executeRestore(
    context: RequestContext,
    params: RestoreExecutionParams
  ): Promise<RestoreExecutionResult> {
    this.auth.require(context, PermissionCode.BackupRestore);

    const startTime = Date.now();
    const backupId = params.backupId.trim();

    // 1. Strict Token Gating
    const expectedToken = generateRestoreConfirmationToken(backupId);
    if (!params.confirmationToken || params.confirmationToken.trim().toUpperCase() !== expectedToken) {
      throw new AppError(
        ErrorCode.Authorization,
        "Token de confirmação inválido para restauração.",
        { safeMessage: `Token de confirmação incorreto. Digite exatamente: ${expectedToken}` }
      );
    }

    // 2. Load package
    let pkg: BackupPackage | null = params.packageData ?? null;
    if (!pkg) {
      pkg = await this.backupRepo.getBackupPackage(backupId);
    }

    if (!pkg) {
      throw new AppError(
        ErrorCode.NotFound,
        `Pacote de backup ${backupId} não encontrado.`,
        { safeMessage: "Pacote de backup não encontrado." }
      );
    }

    // 3. Validate package
    const validation = await this.validateBackupPackage(context, pkg);
    if (!validation.valid) {
      throw new AppError(
        ErrorCode.Validation,
        `Pacote de backup inválido: ${validation.errors.join("; ")}`,
        { safeMessage: `O backup não passou na validação: ${validation.errors.join(". ")}` }
      );
    }

    // 4. Automated Safety Pre-Restore Snapshot
    let safetySnapshotId: string | undefined;
    if (params.createSafetySnapshot !== false) {
      try {
        const snapshot = await this.backupService.createBackup(context, {
          type: "SAFETY_SNAPSHOT",
          description: `Snapshot de segurança automático antes da restauração do backup ${backupId}`
        });
        safetySnapshotId = snapshot.manifest.id;
      } catch (err) {
        console.warn("Falha ao gerar snapshot de segurança pré-restauração", err);
      }
    }

    // 5. Restore Entities
    const restoredEntities: Record<string, number> = {};
    const errors: string[] = [];

    const collectors = this.backupService.getCollectors();
    const collectorMap = new Map<string, BackupEntityCollector>(
      collectors.map(c => [c.entityType, c])
    );

    if (pkg.databasePayload) {
      for (const [entityType, records] of Object.entries(pkg.databasePayload)) {
        if (params.selectiveEntities && !params.selectiveEntities.includes(entityType)) {
          continue;
        }

        const collector = collectorMap.get(entityType);
        if (collector && collector.restoreEntities) {
          try {
            const count = await collector.restoreEntities(context, records);
            restoredEntities[entityType] = count;
          } catch (err: any) {
            const msg = `Erro ao restaurar entidades ${entityType}: ${err?.message ?? String(err)}`;
            console.error(msg, err);
            errors.push(msg);
          }
        } else {
          // Track count even if collector doesn't have custom restore method
          restoredEntities[entityType] = Array.isArray(records) ? records.length : 0;
        }
      }
    }

    const durationMs = Date.now() - startTime;
    const executedAt = new Date().toISOString();
    const executedBy = (context.userId as string) ?? "System";

    const historyEntry: RestoreHistoryEntry = {
      id: `RESTORE-${Date.now()}`,
      restoredAt: executedAt,
      restoredBy: executedBy,
      sourceBackupId: backupId,
      sourceBackupType: pkg.manifest.type,
      safetySnapshotId: safetySnapshotId ?? "NENHUM",
      status: errors.length === 0 ? "SUCCESS" : "FAILED",
      details: errors.length === 0 ? "Restauração concluída com sucesso." : `Restauração concluída com erros: ${errors.join("; ")}`,
      entityCounts: restoredEntities
    };

    await this.backupRepo.addRestoreHistory(historyEntry);

    if (this.audit) {
      await this.audit.record(context, {
        action: AuditAction.BackupRestored,
        entityType: "BackupPackage",
        entityId: backupId,
        previousState: null,
        newState: historyEntry as unknown as JsonObject,
        metadata: {
          backupId,
          ...(safetySnapshotId ? { safetySnapshotId } : {}),
          restoredEntities,
          durationMs
        }
      });
    }

    return {
      success: errors.length === 0,
      backupId,
      ...(safetySnapshotId ? { safetySnapshotId } : {}),
      restoredEntities,
      executedAt,
      executedBy,
      durationMs,
      errors
    };
  }

  async listRestoreHistory(context: RequestContext): Promise<readonly RestoreHistoryEntry[]> {
    this.auth.require(context, PermissionCode.BackupRead);
    const clientId = (context.activeClientId as string) ?? undefined;
    return this.backupRepo.listRestoreHistory(clientId);
  }
}
